import React, { useMemo } from 'react';
import { LuMinus, LuTrendingDown, LuTrendingUp } from 'react-icons/lu';

import {
  CROP_AVERAGE_NO_OTHER_PLANS_MESSAGE,
  CROP_AVERAGE_NO_PLAN_MARGIN_MESSAGE,
  CROP_AVERAGE_TOO_FEW_PLANS_MESSAGE,
} from 'constants/messageConstants';
import {
  CROP_AVERAGE_ABOVE_LABEL,
  CROP_AVERAGE_AVERAGE_LABEL,
  CROP_AVERAGE_BELOW_LABEL,
  CROP_AVERAGE_EQUAL_LABEL,
  CROP_AVERAGE_MIN_OTHER_PLANS,
  CROP_AVERAGE_SAMPLE_LABEL,
  CROP_AVERAGE_THIS_PLAN_LABEL,
  CROP_AVERAGE_TILE_SUBTITLE,
  CROP_AVERAGE_TILE_TITLE,
} from 'constants/uiConstants';
import { useScopedInputs } from 'hooks/useScopedInputs';
import type { GUnit } from 'interfaces/IGlobalCatalogue';
import type { IInput } from 'interfaces/IInput';
import { InputTaskCategory } from 'interfaces/IInputTask';

import { computeCropAverageGm } from './computeScopeContext';
import type { InsightsScopeLevel } from './insightUtils';
import SectionCard from './summary2/SectionCard';

/** "$412/ha" — whole dollars, as a comparison doesn't need cents. */
const formatPerHa = (value: number): string =>
  `${value < 0 ? '−' : ''}$${Math.round(Math.abs(value)).toLocaleString()}/ha`;

interface CropAverageGmTileProps {
  inputs: IInput[];
  scopeLevel?: InsightsScopeLevel;
  activeCategory: InputTaskCategory;
  gUnits: GUnit[];
}

/**
 * Insights › Margin & break-even: one plan's expected GM against the average
 * for the same crop and season across the organisation (OP-2451). The same
 * calculation as Live totals › Financial risk, but on the lens's own Plan /
 * Actual basis, so it never contradicts the matrix beside it.
 *
 * Shown only when exactly one plan is in view — a plan, or a paddock with a
 * single plan for the year. Excluded farms and paddocks are already left out
 * of the organisation's plans (useScopedInputs → useVisibleOrgData).
 */
const CropAverageGmTile: React.FC<CropAverageGmTileProps> = ({
  inputs,
  scopeLevel,
  activeCategory,
  gUnits,
}) => {
  const { orgInputs } = useScopedInputs();
  const isSinglePlan =
    (scopeLevel === 'input' || scopeLevel === 'paddock') &&
    inputs.length === 1;

  const comparison = useMemo(
    () =>
      isSinglePlan
        ? computeCropAverageGm(
            inputs[0],
            orgInputs,
            gUnits,
            new Set([activeCategory])
          )
        : null,
    [isSinglePlan, inputs, orgInputs, gUnits, activeCategory]
  );

  if (!comparison) return null;

  const { cropSeason, planGmPerHa, averageGmPerHa, averagePlanCount } =
    comparison;
  const isActual = activeCategory === InputTaskCategory.Actual;

  const renderBody = () => {
    if (averagePlanCount === 0 || averageGmPerHa === null) {
      return CROP_AVERAGE_NO_OTHER_PLANS_MESSAGE(
        cropSeason.cropName,
        cropSeason.year,
        isActual
      );
    }
    if (averagePlanCount < CROP_AVERAGE_MIN_OTHER_PLANS) {
      return CROP_AVERAGE_TOO_FEW_PLANS_MESSAGE(
        cropSeason.cropName,
        cropSeason.year,
        averagePlanCount,
        CROP_AVERAGE_MIN_OTHER_PLANS
      );
    }
    if (planGmPerHa === null) return CROP_AVERAGE_NO_PLAN_MARGIN_MESSAGE;
    return null;
  };
  const message = renderBody();

  // Rounded to whole dollars first, so "Same as average" matches the figures shown.
  const difference =
    planGmPerHa !== null && averageGmPerHa !== null
      ? Math.round(planGmPerHa) - Math.round(averageGmPerHa)
      : 0;

  return (
    <SectionCard
      id="margin-crop-average"
      title={CROP_AVERAGE_TILE_TITLE}
      subtitle={CROP_AVERAGE_TILE_SUBTITLE(cropSeason.cropName, cropSeason.year)}
      variant="result"
    >
      {message || planGmPerHa === null || averageGmPerHa === null ? (
        <p className="text-base text-gray-dark">{message}</p>
      ) : (
        <div className="flex flex-wrap items-end gap-x-8 gap-y-3">
          <div className="flex flex-col">
            <span className="text-sm text-gray-dark">
              {CROP_AVERAGE_THIS_PLAN_LABEL}
            </span>
            <span className="text-lg font-bold tabular-nums text-charcoal">
              {formatPerHa(planGmPerHa)}
            </span>
          </div>
          <div className="flex flex-col">
            <span className="text-sm text-gray-dark">
              {CROP_AVERAGE_AVERAGE_LABEL}
            </span>
            <span className="text-lg font-bold tabular-nums text-charcoal">
              {formatPerHa(averageGmPerHa)}
            </span>
            <span className="text-sm text-gray-muted">
              {CROP_AVERAGE_SAMPLE_LABEL(
                Math.round(comparison.averageAreaHa).toLocaleString(),
                averagePlanCount
              )}
            </span>
          </div>
          {/* Above / below always says so in words, never colour alone. */}
          {difference === 0 ? (
            <span className="inline-flex items-center gap-1.5 text-base font-semibold text-gray-dark">
              <LuMinus aria-hidden="true" />
              {CROP_AVERAGE_EQUAL_LABEL}
            </span>
          ) : (
            <span
              className={`inline-flex items-center gap-1.5 text-base font-semibold ${
                difference > 0 ? 'text-success' : 'text-danger-dark'
              }`}
            >
              {difference > 0 ? (
                <LuTrendingUp aria-hidden="true" />
              ) : (
                <LuTrendingDown aria-hidden="true" />
              )}
              {formatPerHa(Math.abs(difference))}{' '}
              {difference > 0 ? CROP_AVERAGE_ABOVE_LABEL : CROP_AVERAGE_BELOW_LABEL}
            </span>
          )}
        </div>
      )}
    </SectionCard>
  );
};

export default CropAverageGmTile;
